import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Button, GridRow, GridItem, MenuItem, Select, Spacer, TextField, Typography, useSnackbar } from '@emburse/embark-core';
import { debounce } from 'lodash';
import AceEditor from 'react-ace';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { saveTemplate, getDataInterchangeTemplates, getXMLValidation } from '../../api/dataInterchange';
import useAccountConfig from '../../hooks/useAccountConfig';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'ace-builds/src-noconflict/mode-xml';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'ace-builds/src-noconflict/theme-xcode';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'ace-builds/src-noconflict/ext-language_tools';
function ConfigEditor() {
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const { enqueueSnackbar } = useSnackbar();
    const { accountConfig } = useAccountConfig();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
    }, [enqueueSnackbar]);
    const calcValues = `\t<!-- Delete this section 
\tThe values below will auto convert to the correct value when selected as a Template
\t\t{CompanyName} = Company Name
\t\t{RootDirectory} = Lowest Root Directory (/DI/MyCompany/ versus /DI/MyCompany/Inbound/)
\t\t{ArchiveDirectory} = Lowest Archive Directory (/Archive/MyCompany/ versus /Archive/MyCompany/HRIS/)
\t-->`;
    const genericGetFile = `\t<!--
\tThis action retrieves the oldest file in 'path' that matches 'file' naming pattern.
\t-->
\t<action type="getfile">
\t\t<source host="sftp.certify.com" path="{RootDirectory}" file="{FileName}"/>
\t</action>`;
    const genericArchiveFile = `\t<!--
\tThis action will delete the original file, and place a copy in an archive directory.
\tAutomatically applying a timestamp (yyyyMMddhhmmss) in front of the file extension.
\t-->
\t<action type="archivefile" />`;
    const newTemplate = `<interchange id="{CompanyName} - Action">
${calcValues}\n    
${genericGetFile}\n
\t<!-- Your new action code goes here -->
\t<action type="?"/>\n
${genericArchiveFile}
</interchange>`;
    const [template, setTemplate] = useState({
        name: '',
        data: '',
        validationText: '',
        dataValidation: []
    });
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [refreshTemplates, setRefreshTemplates] = useState(true);
    const [saveDisabled, setSaveDisabled] = useState(true);
    const calculateMaxHeight = () => {
        const maxScreenHeight = window.innerHeight;
        const headerHeight = selectedTemplate === 'New' ? 340 : 300;
        const availableHeight = maxScreenHeight - headerHeight;
        return availableHeight;
    };
    const updateData = (e) => {
        setTemplate((prevState) => ({ ...prevState, data: e }));
        getXMLValidation(e)
            .then((data) => {
            setTemplate((prevState) => ({
                ...prevState,
                dataValidation: JSON.parse(JSON.stringify(data))
            }));
        })
            .catch(handleError);
    };
    const dataChangeHandler = debounce((e) => updateData(e), 1000);
    const selectedTemplateChangeHandler = (e) => {
        const tmpName = e.target.value;
        setSelectedTemplate(tmpName);
        if (tmpName === 'New') {
            setTemplate({
                name: '',
                data: newTemplate,
                validationText: '',
                dataValidation: []
            });
            getXMLValidation(newTemplate);
        }
        else {
            const newData = templates.find((tmp) => tmp.ShardPref_Key2 === tmpName)?.ShardPref_Data ?? '';
            setTemplate({
                name: tmpName,
                data: newData,
                validationText: '',
                dataValidation: []
            });
            getXMLValidation(newData);
        }
    };
    const templateNameChangeHandler = (e) => {
        const tmpName = e.target.value;
        if (tmpName.length > 0 && templates.find((tmp) => tmp.ShardPref_Key2 === tmpName)) {
            setTemplate((prevState) => ({
                ...prevState,
                name: tmpName,
                validationText: 'Please enter in a unique template name, or select it from the dropdown to update.'
            }));
        }
        else {
            setTemplate((prevState) => ({
                ...prevState,
                name: tmpName,
                validationText: ''
            }));
        }
    };
    const submitClickHandler = () => {
        applyLoadingState(30000);
        saveTemplate(template.name, template.data)
            .then(enqueueSnackbar('Template saved successfully!', {
            variant: 'alert',
            AlertProps: { severity: 'success' }
        }))
            .catch(handleError)
            .finally(() => {
            setRefreshTemplates(true);
            removeLoadingState();
        });
    };
    // Update available templates after Save/Create
    useEffect(() => {
        if (refreshTemplates) {
            getDataInterchangeTemplates()
                .then((data) => {
                setTemplates(JSON.parse(JSON.stringify(data)));
            })
                .catch(handleError)
                .finally(() => {
                setRefreshTemplates(false);
            });
        }
    }, [refreshTemplates, handleError]);
    // Validation checks when selected template, or template name, or editor data (based on xmlValidation updating) changes
    useEffect(() => {
        setSaveDisabled(true);
        if (selectedTemplate === 'New') {
            if (template.name.length > 0 &&
                !templates.find((tmp) => tmp.ShardPref_Key2 === template.name) &&
                template.dataValidation.length === 0) {
                setSaveDisabled(false);
            }
        }
        else if (selectedTemplate && template.dataValidation.length === 0) {
            setSaveDisabled(false);
        }
    }, [template, selectedTemplate, templates]);
    return (_jsxs(SingleColumnLayout, { children: [_jsx(Typography, { variant: "h4", children: "Data Interchange Template Config" }), _jsx(Spacer, { size: 32 }), _jsxs(Select, { id: "templateID", label: "Select a template", value: selectedTemplate, onChange: (e) => selectedTemplateChangeHandler(e), children: [templates
                        .sort((a, b) => a.ShardPref_Key2.localeCompare(b.ShardPref_Key2))
                        .map((item) => (_jsx(MenuItem, { value: item.ShardPref_Key2, children: item.ShardPref_Key2 }, item.ShardPref_Key2))), _jsx(MenuItem, { value: "New", children: "New Template" }, "New")] }), _jsx(Spacer, { size: 16 }), selectedTemplate === 'New' && (_jsxs(_Fragment, { children: [_jsx(TextField, { id: "templateName", label: "Template Name", onChange: (e) => templateNameChangeHandler(e), error: template.validationText.length > 0, helperText: template.validationText, required: true, children: template.name }), _jsx(Spacer, { size: 8 })] })), template.dataValidation.length > 0 && (_jsxs(Typography, { variant: "subtitle2", color: "error", children: ["You must resolve any XML errors before", ' ', selectedTemplate === 'New' ? 'creating' : 'saving', "."] })), _jsx(Spacer, { size: 16 }), _jsx(AceEditor, { placeholder: "", mode: "xml", theme: "xcode", showPrintMargin: true, showGutter: true, highlightActiveLine: true, setOptions: {
                    showLineNumbers: true,
                    tabSize: 2
                }, value: template.data, onChange: (e) => dataChangeHandler(e), width: "auto", height: `${calculateMaxHeight()}px`, annotations: template.dataValidation }), _jsx(Spacer, { size: 16 }), _jsx(GridRow, { alignItems: "flex-end", children: _jsx(GridItem, { children: _jsx(Button, { variant: "outlined", disabled: accountConfig.IsAdmin === false ? true : saveDisabled, onClick: submitClickHandler, children: selectedTemplate === 'New' ? 'Create' : 'Save' }) }) })] }));
}
export default ConfigEditor;
